/**
 * Preparo / Montagem assistida de kit.
 *
 * Fluxo:
 *   1. Operador bipa código do kit instance -> carrega receita do template.
 *   2. Operador bipa cada item -> validação client-side contra receita
 *      (mas a validação real é server-side em /kit-instances/:id/assemble).
 *   3. Ao finalizar: POST /v1/kit-instances/:id/assemble (valida matriz térmica + peso)
 *      e em seguida POST /v1/lifecycle/scan target_phase=PREP para avançar a FSM.
 *   4. Abre janela ZPL bruto (text/plain) para impressão.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScanLine, Boxes, Package, Printer, CheckCircle2, Trash2, Send, AlertTriangle } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { printKitLabel } from '@/shared/print/zebra';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface KitByBarcode {
  id: string;
  barcode: string;
  alias?: string | null;
  template_id: string;
  template_name?: string | null;
  estimated_weight_kg?: number | null;
  is_quarantined?: number;
  current_status?: string | null;
}

interface TemplateItem {
  model_id: string;
  model_name: string;
  model_sku: string;
  qty: number;
  requires_functional_check: number;
  weight_kg: number | null;
  steam_compatible: number;
  plasma_compatible: number;
  image_r2_key?: string | null;
}

interface KitTemplate {
  id: string;
  code: string;
  name: string;
  estimated_weight_kg?: number | null;
  prep_sla_minutes?: number | null;
  packaging_id?: string | null;
  items: TemplateItem[];
}

interface ItemByBarcode {
  id: string;
  barcode: string;
  model_id: string;
  model_name?: string;
  weight_kg?: number | null;
  is_quarantined?: number;
}

type FeedbackKind = 'ok' | 'warn' | 'err';
interface Feedback { kind: FeedbackKind; text: string; at: number }

function useBeep() {
  return useCallback((kind: FeedbackKind) => {
    try {
      const AC = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx: AudioContext = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = kind === 'ok' ? 880 : kind === 'warn' ? 540 : 220;
      gain.gain.value = 0.08;
      osc.start();
      osc.stop(ctx.currentTime + (kind === 'err' ? 0.35 : 0.12));
    } catch { /* sem áudio */ }
  }, []);
}

export function PreparoPage() {
  const qc = useQueryClient();
  const beep = useBeep();
  const [kit, setKit] = useState<KitByBarcode | null>(null);
  const [tpl, setTpl] = useState<KitTemplate | null>(null);
  const [items, setItems] = useState<ItemByBarcode[]>([]);
  const [method, setMethod] = useState<'STEAM' | 'PLASMA'>('STEAM');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState(false);
  const [assembling, setAssembling] = useState(false);

  const flash = useCallback((kind: FeedbackKind, text: string) => {
    setFeedback({ kind, text, at: Date.now() });
    beep(kind);
  }, [beep]);

  const onScan = useCallback(async (code: string) => {
    if (busy || assembling) return;
    setBusy(true);
    try {
      if (!kit) {
        const k = await apiGet<KitByBarcode>(`/v1/kit-instances/by-barcode/${encodeURIComponent(code)}`);
        if (k.is_quarantined) { flash('err', `Kit ${k.barcode} em quarentena.`); return; }
        const t = await apiGet<KitTemplate>(`/v1/kit-templates/${k.template_id}`);
        setKit(k); setTpl(t);
        flash('ok', `Receita carregada: ${t.items.length} itens esperados.`);
        return;
      }
      if (items.some((it) => it.barcode === code)) { flash('warn', `Item ${code} já lido.`); return; }
      const it = await apiGet<ItemByBarcode>(`/v1/items/by-barcode/${encodeURIComponent(code)}`);
      if (it.is_quarantined) { flash('err', `Item ${it.barcode} em quarentena.`); return; }

      const expected = tpl?.items.find((x) => x.model_id === it.model_id);
      if (!expected) {
        flash('err', `Item ${it.model_name ?? it.barcode} NÃO pertence a este kit.`);
        return;
      }
      if (method === 'STEAM' && expected.steam_compatible !== 1) {
        flash('err', `Item incompatível com vapor: ${it.model_name}`);
        return;
      }
      if (method === 'PLASMA' && expected.plasma_compatible !== 1) {
        flash('err', `Item incompatível com plasma: ${it.model_name}`);
        return;
      }
      setItems((arr) => [...arr, it]);
      flash('ok', `Item ${expected.model_name} OK.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) flash('err', `Código ${code} não cadastrado.`);
      else flash('err', `Falha: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [busy, assembling, kit, tpl, items, method, flash]);

  useBarcodeScanner({ onScan, minLength: 4 });

  const expectedByModel = useMemo(() => {
    const map = new Map<string, { expected: number; scanned: number; name: string; sku: string; weight_kg: number | null; requires_functional_check: number }>();
    for (const t of tpl?.items ?? []) {
      map.set(t.model_id, {
        expected: t.qty, scanned: 0, name: t.model_name, sku: t.model_sku,
        weight_kg: t.weight_kg, requires_functional_check: t.requires_functional_check,
      });
    }
    for (const it of items) {
      const e = map.get(it.model_id);
      if (e) e.scanned += 1;
    }
    return map;
  }, [tpl, items]);

  const totalWeight = useMemo(() => items.reduce((s, it) => s + (it.weight_kg ?? 0), 0), [items]);
  const allMatched = useMemo(() => {
    if (!tpl) return false;
    for (const e of expectedByModel.values()) if (e.scanned !== e.expected) return false;
    return true;
  }, [tpl, expectedByModel]);

  async function finishAssembly() {
    if (!kit) return;
    setAssembling(true);
    try {
      await apiPost(`/v1/kit-instances/${kit.id}/assemble`, {
        item_ids: items.map((i) => i.id),
        method,
      }, { idempotencyKey: `assemble-${kit.id}-${Date.now()}` });
      // Advança FSM para PREP_DONE
      await apiPost('/v1/lifecycle/scan', {
        barcode: kit.barcode, target_phase: 'PREP',
      }, { idempotencyKey: `prep-${kit.id}-${Date.now()}` });
      flash('ok', 'Kit montado e fase PREP concluída. Imprimindo etiqueta…');
      printZpl(kit.id);
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      // Reset para próximo kit
      setTimeout(() => { setKit(null); setTpl(null); setItems([]); }, 1200);
    } catch (e) {
      if (e instanceof ApiError) flash('err', `${e.code}: ${e.message}`);
      else flash('err', (e as Error).message);
    } finally {
      setAssembling(false);
    }
  }

  async function printZpl(kitId: string) {
    try {
      await printKitLabel(kitId);
    } catch (e) {
      flash('err', (e as Error).message);
    }
  }

  const resetAll = () => { setKit(null); setTpl(null); setItems([]); setFeedback(null); };

  const feedbackTone = useMemo(() => {
    if (!feedback) return '';
    return feedback.kind === 'err'
      ? 'border-danger bg-danger-bg text-danger animate-pulse'
      : feedback.kind === 'warn'
        ? 'border-warning bg-warning-bg text-neutral-900'
        : 'border-success bg-success-bg text-success';
  }, [feedback]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Preparo — Montagem assistida
          </h1>
          <p className="text-sm text-neutral-500">
            Bipe o kit; o sistema mostra a receita. Bipe cada item; valida matriz térmica e peso.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded border border-neutral-300 bg-neutral-0 dark:border-neutral-700 dark:bg-neutral-800">
            <button
              type="button"
              onClick={() => setMethod('STEAM')}
              className={`px-3 py-2 text-xs ${method === 'STEAM' ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200' : 'text-neutral-600 dark:text-neutral-300'}`}
            >Vapor</button>
            <button
              type="button"
              onClick={() => setMethod('PLASMA')}
              className={`px-3 py-2 text-xs ${method === 'PLASMA' ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200' : 'text-neutral-600 dark:text-neutral-300'}`}
            >Plasma</button>
          </div>
          <Button variant="secondary" size="lg" leadingIcon={<Trash2 size={18} />} onClick={resetAll} disabled={!kit && items.length === 0}>
            Limpar
          </Button>
        </div>
      </header>

      {feedback ? (
        <div className={`rounded border-2 px-4 py-3 text-md font-medium ${feedbackTone}`} role="status" aria-live="polite">{feedback.text}</div>
      ) : (
        <div className="rounded border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-500 dark:border-neutral-700">
          Aguardando leitura. Comece pelo código do kit.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Kit em montagem" />
          <CardBody className="space-y-3">
            {kit ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    <Boxes size={24} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-md font-semibold">{kit.alias ?? tpl?.name ?? 'Kit'}</p>
                    <p className="font-mono text-xs text-neutral-500">{kit.barcode}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-y-1 text-xs">
                  <dt className="text-neutral-500">Itens esperados</dt>
                  <dd className="font-medium">{tpl?.items.length ?? 0}</dd>
                  <dt className="text-neutral-500">Peso estimado</dt>
                  <dd className="font-medium">{kit.estimated_weight_kg ?? tpl?.estimated_weight_kg ?? '—'} kg</dd>
                  <dt className="text-neutral-500">Peso atual</dt>
                  <dd className={`font-medium ${tpl?.estimated_weight_kg && totalWeight > tpl.estimated_weight_kg * 1.2 ? 'text-danger' : ''}`}>
                    {totalWeight.toFixed(3)} kg
                  </dd>
                  <dt className="text-neutral-500">Método</dt>
                  <dd className="font-medium">{method}</dd>
                </dl>
              </>
            ) : (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-center">
                <ScanLine size={36} strokeWidth={1.5} className="text-neutral-400" />
                <p className="text-sm text-neutral-500">Bipe o código do kit.</p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Receita do kit"
            subtitle={tpl ? `${items.length}/${tpl.items.reduce((a, b) => a + b.qty, 0)} itens lidos` : 'Aguardando kit'}
            action={allMatched ? <Badge tone="success">Completo</Badge> : tpl ? <Badge tone="warning">Em andamento</Badge> : null}
          />
          <CardBody className="p-0">
            <ul className="max-h-96 divide-y divide-neutral-200 overflow-y-auto dark:divide-neutral-700">
              {!tpl ? (
                <li className="grid place-items-center p-10 text-sm text-neutral-500">
                  <Package size={36} strokeWidth={1.5} className="mb-2 text-neutral-400" />
                  Receita aparecerá após o kit ser lido.
                </li>
              ) : tpl.items.map((t) => {
                const c = expectedByModel.get(t.model_id);
                const scanned = c?.scanned ?? 0;
                const complete = scanned >= t.qty;
                const over = scanned > t.qty;
                return (
                  <li key={t.model_id} className={`flex items-center justify-between gap-3 px-4 py-2 ${over ? 'bg-danger-bg/30' : complete ? 'bg-success-bg/30' : ''}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`grid h-7 w-7 place-items-center rounded text-xs font-semibold ${complete ? 'bg-success text-white' : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200'}`}>
                        {scanned}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{t.model_name}</p>
                        <p className="font-mono text-xxs text-neutral-500">{t.model_sku}{t.requires_functional_check ? ' · check funcional' : ''}</p>
                      </div>
                    </div>
                    <span className="text-xs text-neutral-500">esperado {t.qty}</span>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
        {tpl && !allMatched ? (
          <span className="flex items-center gap-1 text-xs text-neutral-500">
            <AlertTriangle size={14} /> A finalização exige todos os itens da receita.
          </span>
        ) : null}
        <Button
          variant="primary"
          size="touch"
          leadingIcon={assembling ? null : <Send size={18} />}
          loading={assembling}
          disabled={!kit || !allMatched}
          onClick={finishAssembly}
        >
          {assembling ? 'Finalizando…' : 'Finalizar montagem + imprimir'}
        </Button>
        {kit ? (
          <Button variant="secondary" size="touch" leadingIcon={<Printer size={18} />} onClick={() => printZpl(kit.id)}>
            Reimprimir etiqueta
          </Button>
        ) : null}
        {assembling ? <CheckCircle2 className="text-success" size={20} /> : null}
      </div>
    </div>
  );
}
